import { FC, forwardRef } from 'react';
import styled, { CSSObject, StyledComponentPropsWithRef } from 'styled-components';

import { BORDER_RADIUS, COLOURS, getBodyFont } from '@belong/themes';
import { ITag, TAG_VARIANT } from '@belong/types';

const getTagStyles = ({ variant = TAG_VARIANT.NEUTRAL_LIGHT, solid }: ITag) => {
  const tagStylesMap: { [key in TAG_VARIANT]: CSSObject } = {
    [TAG_VARIANT.BRAND]: {
      color: COLOURS.GREY_700,
      borderColor: solid ? COLOURS.BELONG_BLUE : COLOURS.MID_BLUE,
      background: solid ? COLOURS.BELONG_BLUE : COLOURS.LIGHT_BLUE
    },
    [TAG_VARIANT.ACCENT1]: {
      color: COLOURS.GREY_700,
      borderColor: COLOURS.DISCO_PINK_DARK,
      background: solid ? COLOURS.DISCO_PINK_DARK : COLOURS.DISCO_PINK
    },
    [TAG_VARIANT.ACCENT2]: {
      color: COLOURS.GREY_700,
      borderColor: COLOURS.PEPPERMINT_CRISP_DARK,
      background: solid ? COLOURS.PEPPERMINT_CRISP_DARK : COLOURS.PEPPERMINT_CRISP
    },
    [TAG_VARIANT.ACCENT3]: {
      color: COLOURS.GREY_700,
      borderColor: COLOURS.PASSION_POP_DARK,
      background: solid ? COLOURS.PASSION_POP_DARK : COLOURS.PASSION_POP
    },
    [TAG_VARIANT.NEUTRAL_LIGHT]: {
      color: solid ? COLOURS.GREY_100 : COLOURS.GREY_600,
      borderColor: solid ? COLOURS.GREY_500 : COLOURS.GREY_300,
      background: solid ? COLOURS.GREY_500 : COLOURS.GREY_100
    },
    [TAG_VARIANT.NEUTRAL_DARK]: {
      color: solid ? COLOURS.GREY_100 : COLOURS.GREY_700,
      borderColor: COLOURS.GREY_700,
      background: solid ? COLOURS.GREY_700 : COLOURS.GREY_100
    }
  };

  return tagStylesMap[variant];
};

export const TagGroup = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spaces.xxsmall}${theme.cssUnit}`};
`;

const StyledTag = styled.span<ITag>`
  display: inline-block;
  vertical-align: middle;

  padding: ${({ theme }) => `${theme.spaces.xxsmall}${theme.cssUnit} ${theme.spaces.xsmall}${theme.cssUnit}`};
  ${getBodyFont('xs', 'small')}

  border: solid 1px;
  border-radius: ${BORDER_RADIUS.INPUT};

  ${getTagStyles}
`;

type StyledTagProps = ITag & StyledComponentPropsWithRef<typeof StyledTag>;

// Wrapper for mapping label -> children
export const Tag: FC<StyledTagProps> = forwardRef(({ label, ...props }, ref) => (
  <StyledTag {...props} ref={ref}>
    {label}
  </StyledTag>
));

Tag.displayName = 'Tag';
